import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { currentYear } from "@/lib/auswertungen";
import {
  fetchInvoiceRevenueReconcile,
  fetchInvoiceRevenueReport,
  formatDateShortDe,
  formatDateTimeDe,
  formatEuroCompact,
  formatEuroDe,
  INVOICE_EXCLUDE_REASON_LABELS,
  savePriorYearVorkasse,
  type InvoiceRevenueAmountSummary,
  type InvoiceRevenueAuftragsart,
  type InvoiceRevenueCustomer,
  type InvoiceRevenueExcludeReason,
  type InvoiceRevenueForecast,
  type InvoiceRevenueReconcileDoc,
  type InvoiceRevenueReconcileReport,
  type InvoiceRevenueReconcileVariant,
  type InvoiceRevenueReport as InvoiceRevenueReportData,
} from "@/lib/heroDocumentReports";
import { toast } from "@/lib/toast";
import { CustomerRevenuePie } from "./CustomerRevenuePie";
import { MonthlyStackedBars } from "./MonthlyStackedBars";
import { ReportYearPicker } from "./ReportYearPicker";
import "./auswertungen.css";

const HERO_TARGET_DEFAULT = 816303.29;

const FORECAST_CONFIDENCE_LABELS: Record<InvoiceRevenueForecast["confidence"], string> = {
  unsicher: "Noch unsicher – Jahresanfang",
  tendenz: "Erste Tendenz",
  belastbar: "Belastbare Tendenz",
  valide: "Valide Prognose",
  ist: "Ist-Wert, Jahr abgeschlossen",
};

const CUSTOMER_COLUMNS: Column<InvoiceRevenueCustomer>[] = [
  { key: "kunde", header: "Kunde", render: (row) => row.kunde || "Unbekannt" },
  {
    key: "invoice_count",
    header: "Rechnungen",
    render: (row) => <span className="tabular-nums">{row.invoice_count}</span>,
  },
  {
    key: "total_amount",
    header: "Umsatz",
    render: (row) => (
      <span className="tabular-nums font-medium">{formatEuroDe(row.total_amount)}</span>
    ),
  },
  {
    key: "paid_amount",
    header: "Bezahlt",
    render: (row) => (
      <span className="tabular-nums text-label-secondary">{formatEuroDe(row.paid_amount)}</span>
    ),
  },
  {
    key: "open_amount",
    header: "Offen",
    render: (row) =>
      row.open_amount > 0 ? (
        <span className="eh-docreport-amount--open tabular-nums font-medium">
          {formatEuroDe(row.open_amount)}
        </span>
      ) : (
        <span className="text-label-tertiary">–</span>
      ),
  },
];

const AUFTRAGSART_COLUMNS: Column<InvoiceRevenueAuftragsart>[] = [
  { key: "auftragsart", header: "Auftragsart", render: (row) => row.auftragsart || "Unbekannt" },
  {
    key: "invoice_count",
    header: "Rechnungen",
    render: (row) => <span className="tabular-nums">{row.invoice_count}</span>,
  },
  {
    key: "total_amount",
    header: "Umsatz",
    render: (row) => (
      <span className="tabular-nums font-medium">{formatEuroDe(row.total_amount)}</span>
    ),
  },
];

const VARIANT_COLUMNS: Column<InvoiceRevenueReconcileVariant>[] = [
  {
    key: "label",
    header: "Filter-Variante",
    render: (row) => row.label,
  },
  {
    key: "invoice_count",
    header: "Belege",
    render: (row) => <span className="tabular-nums">{row.invoice_count}</span>,
  },
  {
    key: "total_amount",
    header: "Summe",
    render: (row) => (
      <span className="tabular-nums font-medium">{formatEuroDe(row.total_amount)}</span>
    ),
  },
  {
    key: "diff_to_target",
    header: "Diff. zu Hero",
    render: (row) => (
      <span className="tabular-nums text-label-secondary">
        {formatEuroDe(row.diff_to_target)}
      </span>
    ),
  },
];

const EXCLUDED_COLUMNS: Column<InvoiceRevenueReconcileDoc>[] = [
  {
    key: "document_number",
    header: "Nummer",
    render: (row) => row.document_number || `Hero #${row.hero_document_id}`,
  },
  {
    key: "document_type",
    header: "Typ",
    render: (row) => row.document_type || "–",
  },
  {
    key: "document_status_name",
    header: "Status",
    render: (row) => row.document_status_name || "–",
  },
  {
    key: "booking_status_name",
    header: "Zahlstatus",
    render: (row) => row.booking_status_name || "–",
  },
  {
    key: "belegdatum",
    header: "Belegdatum",
    render: (row) => formatDateShortDe(row.belegdatum),
  },
  {
    key: "total_amount",
    header: "Netto",
    render: (row) => (
      <span className="tabular-nums font-medium">{formatEuroDe(row.total_amount)}</span>
    ),
  },
  {
    key: "exclude_reason",
    header: "Grund",
    render: (row) =>
      INVOICE_EXCLUDE_REASON_LABELS[row.exclude_reason as InvoiceRevenueExcludeReason] ||
      row.exclude_reason,
  },
];

function emptyAmountSummary(): InvoiceRevenueAmountSummary {
  return { invoice_count: 0, total_amount: 0, paid_amount: 0, open_amount: 0 };
}

export function InvoiceRevenueReport() {
  const [year, setYear] = useState(() => currentYear());
  const [report, setReport] = useState<InvoiceRevenueReportData | null>(null);
  const [reconcile, setReconcile] = useState<InvoiceRevenueReconcileReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [vorkasseInput, setVorkasseInput] = useState("");
  const [savingVorkasse, setSavingVorkasse] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchInvoiceRevenueReport(year);
      setReport(data);
      setVorkasseInput(formatEuroInput(data.prior_year_vorkasse ?? 0));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Umsatz-Auswertung konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [year]);

  const loadReconcile = useCallback(async () => {
    setReconcileLoading(true);
    try {
      const data = await fetchInvoiceRevenueReconcile(year, HERO_TARGET_DEFAULT);
      setReconcile(data);
      setReconcileError(null);
    } catch (err) {
      setReconcileError(
        err instanceof ApiError ? err.message : "Abstimmung konnte nicht geladen werden.",
      );
    } finally {
      setReconcileLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void load();
    setReconcile(null);
    setShowReconcile(false);
  }, [load]);

  useEffect(() => {
    if (showReconcile && !reconcile && !reconcileLoading) {
      void loadReconcile();
    }
  }, [showReconcile, reconcile, reconcileLoading, loadReconcile]);

  async function handleSaveVorkasse() {
    const amount = parseEuroInput(vorkasseInput);
    if (amount == null) {
      toast.error("Bitte einen gültigen Betrag eingeben.");
      return;
    }
    setSavingVorkasse(true);
    try {
      await savePriorYearVorkasse(year, amount);
      toast.success("Vorkasse aus dem Vorjahr gespeichert.");
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Vorkasse konnte nicht gespeichert werden.",
      );
    } finally {
      setSavingVorkasse(false);
    }
  }

  const summary = report?.summary;
  const heroSummary = report?.hero_summary ?? emptyAmountSummary();
  const appSummary = report?.app_summary ?? emptyAmountSummary();
  const heroDiffToTarget = useMemo(
    () => round2(heroSummary.total_amount - HERO_TARGET_DEFAULT),
    [heroSummary.total_amount],
  );

  const sourceChartData = useMemo(
    () =>
      (report?.by_month ?? []).map((m) => ({
        month: m.month,
        values: {
          hero: m.hero_amount ?? 0,
          app: m.app_amount ?? 0,
          draft: m.draft_amount ?? 0,
        },
      })),
    [report?.by_month],
  );

  const paidOpenChartData = useMemo(
    () =>
      (report?.by_month ?? []).map((m) => ({
        month: m.month,
        values: {
          paid: m.paid_amount,
          open: m.open_amount,
          draft: m.draft_amount ?? 0,
        },
      })),
    [report?.by_month],
  );

  const customerPieItems = useMemo(
    () =>
      (report?.top_customers ?? []).map((c) => ({
        key: `${c.kunden_id ?? c.kunde}`,
        label: c.kunde || "Unbekannt",
        value: c.total_amount,
      })),
    [report?.top_customers],
  );

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Umsatz-Auswertung konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <ReportYearPicker
          year={year}
          onYearChange={setYear}
          loading={loading}
          onReload={() => void load()}
        />
        <p className="mt-3 text-xs text-label-secondary">
          Gesamtumsatz aus Hero-Rechnungen und App-eigenen Rechnungen (netto, ohne Entwürfe).
          Vorkasse aus dem Vorjahr kann manuell ergänzt werden – dann ergibt sich der valide
          Jahresumsatz. Die Prognose rechnet den bisherigen Tagesdurchschnitt auf das Jahresende hoch.
          Entwürfe erscheinen in den Monatsdiagrammen grau und zählen nicht zum Umsatz.
          Hero wie das Hero-Dashboard „Rechnungsvolumen“: Belegdatum, ohne Status Storniert und
          ohne Stornorechnungen; bezahlt + offen = Umsatz. App-eigene Rechnungen werden addiert.
          {report?.last_synced_at
            ? ` Letzter Hero-Sync: ${formatDateTimeDe(report.last_synced_at)}.`
            : ""}
        </p>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Umsatz-Auswertung wird geladen …</span>
        </div>
      ) : report ? (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Gesamtumsatz {year}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatEuroCompact(summary?.total_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.invoice_count ?? 0} Rechnungen (Hero + App)
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Davon bezahlt</p>
              <p className="eh-docreport-amount--paid mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.paid_amount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Noch offen</p>
              <p className="eh-docreport-amount--open mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.open_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                netto, nur Rechnungen {year} – Offene Posten zeigt brutto über alle Jahre
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Ø Zahlungsdauer (Hero)</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {summary?.avg_payment_days != null
                  ? `${String(summary.avg_payment_days).replace(".", ",")} Tage`
                  : "–"}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.median_payment_days != null
                  ? `Median: ${String(summary.median_payment_days).replace(".", ",")} Tage (${summary.paid_with_date_count} bezahlte Hero-Rechnungen)`
                  : "Keine bezahlten Hero-Rechnungen mit Zahldatum"}
              </p>
            </div>
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-label-secondary">
                    Vorkasse aus {year - 1}
                  </p>
                  <p className="mt-1 text-xs text-label-secondary">
                    Wird zum Rechnungsumsatz addiert. Der Betrag bleibt fest und wird nicht
                    hochgerechnet.
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <TextInput
                      label="Betrag"
                      inputMode="decimal"
                      value={vorkasseInput}
                      onChange={(e) => setVorkasseInput(e.target.value)}
                      placeholder="0,00"
                      hint="Euro, z. B. 125.000,00"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={savingVorkasse || loading}
                    loading={savingVorkasse}
                    onClick={() => void handleSaveVorkasse()}
                  >
                    Speichern
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-ios-md bg-background px-3 py-3 ring-1 ring-separator/30">
                  <p className="text-xs text-label-secondary">Valider Umsatz {year}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                    {formatEuroCompact(report.valid_revenue ?? summary?.total_amount)}
                  </p>
                  <p className="mt-1 text-[11px] text-label-tertiary">
                    Rechnungen {formatEuroCompact(summary?.total_amount)}
                    {(report.prior_year_vorkasse ?? 0) > 0
                      ? ` + Vorkasse ${formatEuroCompact(report.prior_year_vorkasse)}`
                      : " · ohne Vorkasse-Zuschlag"}
                  </p>
                </div>
                <div className="rounded-ios-md bg-background px-3 py-3 ring-1 ring-separator/30">
                  <p className="text-xs text-label-secondary">
                    {report.forecast?.year_complete
                      ? "Jahresumsatz (Ist)"
                      : "Prognose Jahresende"}
                  </p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                    {formatEuroCompact(report.forecast?.projected_valid_amount)}
                  </p>
                  <p className="mt-1 text-[11px] text-label-tertiary">
                    {report.forecast
                      ? FORECAST_CONFIDENCE_LABELS[report.forecast.confidence]
                      : "–"}
                  </p>
                </div>
              </div>
            </div>
            {report.forecast ? (
              <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div>
                  <p className="text-[11px] text-label-tertiary">Jahr fortgeschritten</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums text-label">
                    {formatPercentFromRatio(report.forecast.elapsed_ratio)}
                  </p>
                  <p className="text-[11px] text-label-tertiary">
                    {report.forecast.elapsed_days} von {report.forecast.days_in_year} Tagen
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-label-tertiary">Ø Umsatz / Tag</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums text-label">
                    {formatEuroDe(report.forecast.average_per_day)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-label-tertiary">Ø Umsatz / Monat</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums text-label">
                    {formatEuroDe(report.forecast.average_per_month)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-label-tertiary">Hochrechnung Rechnungen</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums text-label">
                    {formatEuroCompact(report.forecast.projected_invoice_amount)}
                  </p>
                  <p className="text-[11px] text-label-tertiary">ohne Vorkasse-Zuschlag</p>
                </div>
              </div>
            ) : null}
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <div className="flex items-center gap-2">
                <span
                  className="eh-docreport-bar__segment eh-docreport-bar__segment--hero inline-block h-2.5 w-2.5 rounded-sm"
                  aria-hidden
                />
                <p className="text-xs text-label-secondary">davon Hero</p>
              </div>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuroCompact(heroSummary.total_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {heroSummary.invoice_count} Belege · bezahlt{" "}
                {formatEuroCompact(heroSummary.paid_amount)} · offen{" "}
                {formatEuroCompact(heroSummary.open_amount)}
              </p>
              <p className="mt-2 text-[11px] text-label-tertiary">
                Diff. zu Hero-Dashboard ({formatEuroCompact(HERO_TARGET_DEFAULT)}):{" "}
                <span className="tabular-nums font-medium text-label">
                  {formatEuroDe(heroDiffToTarget)}
                </span>
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <div className="flex items-center gap-2">
                <span
                  className="eh-docreport-bar__segment eh-docreport-bar__segment--app inline-block h-2.5 w-2.5 rounded-sm"
                  aria-hidden
                />
                <p className="text-xs text-label-secondary">davon App</p>
              </div>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuroCompact(appSummary.total_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {appSummary.invoice_count} Rechnungen · bezahlt{" "}
                {formatEuroCompact(appSummary.paid_amount)} · offen{" "}
                {formatEuroCompact(appSummary.open_amount)}
              </p>
            </div>
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium text-label-secondary">Abstimmung Hero</p>
              <Button
                variant="secondary"
                size="sm"
                disabled={reconcileLoading}
                onClick={() => {
                  if (showReconcile) {
                    setShowReconcile(false);
                    return;
                  }
                  setShowReconcile(true);
                  if (reconcile) void loadReconcile();
                }}
              >
                {showReconcile ? "Abstimmung ausblenden" : "Abstimmung laden"}
              </Button>
            </div>
            <p className="text-xs text-label-secondary">
              Vergleicht den App-Hero-Umsatz mit dem Hero-Dashboard-Wert und listet Filter-Varianten
              sowie ausgeschlossene Belege auf.
            </p>

            {showReconcile ? (
              <div className="mt-4 space-y-4">
                {reconcileError ? (
                  <Alert variant="error" title="Abstimmung konnte nicht geladen werden">
                    {reconcileError}
                  </Alert>
                ) : null}
                {reconcileLoading && !reconcile ? (
                  <div className="flex items-center text-label-secondary">
                    <Spinner />
                    <span className="ml-2 text-sm">Abstimmung wird geladen …</span>
                  </div>
                ) : null}
                {reconcile ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="rounded-ios-md bg-background px-3 py-2 ring-1 ring-separator/30">
                        <p className="text-[11px] text-label-tertiary">App Hero</p>
                        <p className="text-lg font-semibold tabular-nums">
                          {formatEuroCompact(reconcile.current.total_amount)}
                        </p>
                        <p className="text-[11px] text-label-tertiary">
                          {reconcile.current.invoice_count} Belege
                        </p>
                      </div>
                      <div className="rounded-ios-md bg-background px-3 py-2 ring-1 ring-separator/30">
                        <p className="text-[11px] text-label-tertiary">Hero-Ziel</p>
                        <p className="text-lg font-semibold tabular-nums">
                          {formatEuroCompact(reconcile.hero_target)}
                        </p>
                      </div>
                      <div className="rounded-ios-md bg-background px-3 py-2 ring-1 ring-separator/30">
                        <p className="text-[11px] text-label-tertiary">Differenz App − Hero</p>
                        <p className="text-lg font-semibold tabular-nums">
                          {formatEuroDe(reconcile.current.diff_to_target)}
                        </p>
                        <p className="text-[11px] text-label-tertiary">
                          Beste Variante:{" "}
                          {reconcile.variants.find((v) => v.key === reconcile.best_variant_key)
                            ?.label || reconcile.best_variant_key}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-label">Filter-Varianten</h3>
                      <Table
                        columns={VARIANT_COLUMNS}
                        rows={reconcile.variants}
                        rowKey={(row) => row.key}
                        emptyText="Keine Varianten."
                      />
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-label">
                        Belege außerhalb der besten Variante
                      </h3>
                      <p className="text-xs text-label-secondary">
                        {reconcile.excluded_documents_count} Belege · Summe{" "}
                        {formatEuroDe(reconcile.excluded_documents_sum)} (max. 200 Zeilen)
                      </p>
                      <Table
                        columns={EXCLUDED_COLUMNS}
                        rows={reconcile.excluded_documents}
                        rowKey={(row) => String(row.hero_document_id)}
                        emptyText="Keine abweichenden Belege – Abstimmung passt."
                      />
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <p className="mb-3 text-xs font-medium text-label-secondary">
              Monatsumsatz gesamt (Hero + App)
            </p>
            <MonthlyStackedBars
              data={sourceChartData}
              series={[
                { key: "hero", label: "Hero", variant: "hero" },
                { key: "app", label: "App", variant: "app" },
                { key: "draft", label: "Entwürfe", variant: "draft", excludeFromTotal: true },
              ]}
              formatValue={formatEuroCompact}
              emptyText={`Keine Rechnungen für ${year} gefunden.`}
            />
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <p className="mb-3 text-xs font-medium text-label-secondary">
              Monatsumsatz gesamt (bezahlt / offen)
            </p>
            <MonthlyStackedBars
              data={paidOpenChartData}
              series={[
                { key: "paid", label: "Bezahlt", variant: "paid" },
                { key: "open", label: "Offen", variant: "open" },
                { key: "draft", label: "Entwürfe", variant: "draft", excludeFromTotal: true },
              ]}
              formatValue={formatEuroCompact}
              emptyText={`Keine Rechnungen für ${year} gefunden.`}
            />
          </section>

          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <p className="mb-3 text-xs font-medium text-label-secondary">
              Top-Kunden nach Gesamtumsatz (Anteil am Jahresumsatz)
            </p>
            <CustomerRevenuePie
              items={customerPieItems}
              totalRevenue={summary?.total_amount ?? 0}
              emptyText={`Keine Rechnungen für ${year} gefunden.`}
            />
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-label">Top-Kunden</h3>
              <Table
                columns={CUSTOMER_COLUMNS}
                rows={report.top_customers}
                rowKey={(row) => `${row.kunden_id ?? row.kunde}`}
                emptyText={`Keine Rechnungen für ${year} gefunden.`}
              />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-label">Nach Auftragsart (nur Hero)</h3>
              <Table
                columns={AUFTRAGSART_COLUMNS}
                rows={report.by_auftragsart}
                rowKey={(row) => `${row.auftragsart}`}
                emptyText={`Keine Hero-Rechnungen für ${year} gefunden.`}
              />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatEuroInput(amount: number): string {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function parseEuroInput(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, "");
  if (!trimmed) return 0;
  let normalized = trimmed;
  if (trimmed.includes(",") && trimmed.includes(".")) {
    const lastComma = trimmed.lastIndexOf(",");
    const lastDot = trimmed.lastIndexOf(".");
    normalized =
      lastComma > lastDot
        ? trimmed.replace(/\./g, "").replace(",", ".")
        : trimmed.replace(/,/g, "");
  } else if (trimmed.includes(",")) {
    normalized = trimmed.replace(/\./g, "").replace(",", ".");
  }
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return round2(value);
}

function formatPercentFromRatio(ratio: number): string {
  if (!Number.isFinite(ratio)) return "–";
  return `${(ratio * 100).toFixed(1).replace(".", ",")} %`;
}
